import React from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
import { getLanguagesByAppLanguage } from '../data/languages';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiClock, FiTrash2, FiArrowRight } = FiIcons;

const TranslationHistory = ({ history, onSelectTranslation, onClearHistory }) => {
  const { t, currentLanguage } = useLanguage();

  if (history.length === 0) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.4 }}
        className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-lg rounded-2xl shadow-xl border border-gray-200/20 dark:border-gray-700/20 p-6"
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold text-gray-800 dark:text-white flex items-center">
            <SafeIcon icon={FiClock} className="w-5 h-5 mr-2" />
            {t('translationHistory')}
          </h3>
        </div>
        <div className="text-center py-8">
          <SafeIcon icon={FiClock} className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500 dark:text-gray-400">{t('noHistory')}</p>
        </div>
      </motion.div>
    );
  }

  const getLanguageName = (code) => {
    if (code === 'auto') return t('detectLanguage');
    const languageNames = getLanguagesByAppLanguage(currentLanguage);
    return languageNames[code] || code;
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.4 }}
      className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-lg rounded-2xl shadow-xl border border-gray-200/20 dark:border-gray-700/20 p-6"
    >
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-bold text-gray-800 dark:text-white flex items-center">
          <SafeIcon icon={FiClock} className="w-5 h-5 mr-2" />
          {t('translationHistory')}
        </h3>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={onClearHistory}
          className="flex items-center space-x-2 px-3 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg text-sm font-medium transition-all duration-200 flex-shrink-0"
        >
          <SafeIcon icon={FiTrash2} className="w-4 h-4" />
          <span>{t('clearHistory')}</span>
        </motion.button>
      </div>
      <div className="space-y-3 max-h-96 overflow-y-auto overflow-x-hidden">
        {history.map((entry, index) => (
          <motion.div
            key={entry.id}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3, delay: index * 0.05 }}
            whileHover={{ scale: 1.02 }}
            onClick={() => onSelectTranslation(entry)}
            className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 hover:border-indigo-300 dark:hover:border-indigo-500 cursor-pointer transition-all duration-200 min-w-0"
          >
            <div className="flex items-center justify-between mb-2 min-w-0">
              <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400 flex-shrink-0">
                <span className="truncate">{getLanguageName(entry.sourceLanguage)}</span>
                <SafeIcon icon={FiArrowRight} className="w-4 h-4 flex-shrink-0" />
                <span className="truncate">{getLanguageName(entry.targetLanguage)}</span>
              </div>
              <span className="text-xs text-gray-500 dark:text-gray-400 flex-shrink-0 ml-2">
                {formatTime(entry.timestamp)}
              </span>
            </div>
            <div className="grid md:grid-cols-2 gap-3 min-w-0">
              <div className="min-w-0">
                <p className="text-sm text-gray-800 dark:text-gray-200 line-clamp-2 break-words">
                  {entry.sourceText}
                </p>
              </div>
              <div className="border-l border-gray-300 dark:border-gray-600 pl-3 min-w-0">
                <p className="text-sm text-gray-800 dark:text-gray-200 line-clamp-2 break-words">
                  {entry.translatedText}
                </p>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </motion.div>
  );
};

export default TranslationHistory;